import { GetAdminUsersResponse } from '@orbit/api-types'
import { useAdminFilter } from '@pages/Admin/AdminFilterContext'
import api from '@lib/api'
import { queryKeys } from '@lib/queryKeys'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

interface UseAdminUsersParams {
	page: number
	pageSize?: number
	/** Search by name or email; falsy values are omitted so the server returns everything. */
	q?: string
}

/**
 * Fetches a page of the admin users list (GET /v1/admin/users), scoped to the
 * global creation-date range from {@link useAdminFilter} plus the caller's page
 * and search term. All three are part of the query key, so changing any of them
 * refetches. Previous data is kept while the next page loads to avoid a flash of
 * empty table. Gated server-side by the AdminGuard; non-admins receive 403.
 */
export const useAdminUsers = ({ page, pageSize, q }: UseAdminUsersParams) => {
	const { from, to, includeInternal } = useAdminFilter()

	return useQuery<GetAdminUsersResponse>({
		queryKey: queryKeys.adminUsers(from, to, page, pageSize, q, includeInternal),
		queryFn: async () => {
			const response = await api.get<GetAdminUsersResponse>('/admin/users', {
				params: { from, to, page, pageSize, q: q || undefined, includeInternal: includeInternal || undefined },
			})
			return response.data
		},
		placeholderData: keepPreviousData,
	})
}
